import { plannerMessage } from '@shared/models/error-code.model';
import { RouterLink } from '@angular/router';
import {
  Component,
  input,
  output,
  inject,
  ChangeDetectionStrategy,
  computed,
  signal,
  effect,
  OnDestroy,
} from '@angular/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideTrendingUp,
  lucideDollarSign,
  lucideZap,
  lucideLeaf,
  lucideCalendar,
  lucideCheck,
  lucideX,
  lucideSettings,
  lucideCalculator,
  lucideSun,
  lucideBatteryCharging,
  lucideFileCog,
  lucideChartLine,
  lucideInfo,
  lucideLoader,
  lucideCircleAlert,
  lucideChevronDown,
  lucidePlus,
  lucideTrash2,
  lucideStar,
  lucideRotateCcw,
  lucideFileDown,
  lucideRefreshCw, lucideExternalLink,
} from '@ng-icons/lucide';
import { BrnMenuTriggerDirective } from '@spartan-ng/brain/menu';
import { HlmBadgeDirective } from '@spartan-ng/helm/badge';
import { HlmMenuImports } from '@spartan-ng/helm/menu';
import { NgApexchartsModule } from 'ng-apexcharts';
import type {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexYAxis,
  ApexStroke,
  ApexFill,
  ApexTooltip,
  ApexLegend,
  ApexDataLabels,
  ApexPlotOptions,
  ApexGrid,
} from 'ng-apexcharts';

import {
  Analysis,
  AnalysisPlanning,
  PlanningChartData,
  ReportExport,
} from '@shared/models/analysis.model';
import { AnalysisService } from '@shared/services/analysis.service';
import { ToastService } from '@shared/services/toast.service';
import { AnalysisDetailStore } from '@features/analyses/store/analysis-detail.store';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { StatePanel } from '@shared/components/state-panel/state-panel';

export interface LineChartOptions {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  stroke: ApexStroke;
  fill: ApexFill;
  tooltip: ApexTooltip;
  legend: ApexLegend;
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  colors: string[];
}

export interface BarChartOptions {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  stroke: ApexStroke;
  fill: ApexFill;
  tooltip: ApexTooltip;
  legend: ApexLegend;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  grid: ApexGrid;
  colors: string[];
}

const MONTHS_DE = [
  'Jan',
  'Feb',
  'Mär',
  'Apr',
  'Mai',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Okt',
  'Nov',
  'Dez',
];
const HOURS = Array.from({ length: 24 }, (_, i) => `${i}:00`);

interface PlanningRequest {
  name: string;
  requestedAt: string;
  powerConsumption?: number;
  electricityPrice?: number;
  templateId?: string;
}

@Component({
  selector: 'app-analysis-plannings',
  standalone: true,
  imports: [
    RouterLink,
    NgIconComponent,
    NgApexchartsModule,
    HlmBadgeDirective,
    BrnMenuTriggerDirective,
    ...HlmMenuImports,
    StatePanel,
  ],
  providers: [
    provideIcons({
      lucideTrendingUp,
      lucideDollarSign,
      lucideZap,
      lucideLeaf,
      lucideCalendar,
      lucideCheck,
      lucideX,
      lucideSettings,
      lucideCalculator,
      lucideSun,
      lucideBatteryCharging,
      lucideFileCog,
      lucideChartLine,
      lucideInfo,
      lucideLoader,
      lucideCircleAlert,
      lucideChevronDown,
      lucidePlus,
      lucideTrash2,
      lucideStar,
      lucideRotateCcw,
      lucideFileDown,
      lucideRefreshCw, lucideExternalLink,
    }),
  ],
  templateUrl: './analysis-plannings.html',
  styleUrl: './analysis-plannings.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalysisPlannings implements OnDestroy {
  readonly plannerMessage = plannerMessage;
  private readonly analysisDetailStore = inject(AnalysisDetailStore, {
    optional: true,
  });
  private readonly currentOrgStore = inject(CurrentOrganizationStore, {
    optional: true,
  });
  private readonly analysisService = inject(AnalysisService);
  private readonly toastService = inject(ToastService);
  private reportExportPollTimer: number | null = null;
  private reportExportRequest = 0;
  private reportExportPlanningId: string | null = null;

  // ─── Inputs ───────────────────────────────────────────────────────────────

  plannings = input<AnalysisPlanning[]>([]);
  analysis = input<Analysis | null>(null);
  section = input<'summary' | 'template' | 'charts' | 'all'>('all');
  selectedPlanningId = input<string | null>(null);
  primaryPlanningId = input<string | null>(null);
  mode = input<'admin' | 'public'>('admin');
  /** One line instead of large panels for running, failed and cancelled plannings. */
  compact = input(false);
  canCreatePlanning = input(false);
  isVisible = input<boolean>(true);
  chartDataProvider = input<
    ((planningId: string) => Promise<PlanningChartData | null>) | null
  >(null);
  selectedPlanningIdChange = output<string>();
  isDeletingPlanning = input(false);
  deletePlanning = output<string>();
  setPrimaryPlanning = output<string>();
  createPlanning = output<void>();
  retryPlanning = output<string>();
  activeEnergyChart = signal<'monthly' | 'daily'>('monthly');
  reportExport = signal<ReportExport | null>(null);
  reportExportBusy = signal(false);

  // ─── Derived context ──────────────────────────────────────────────────────

  readonly catalogBase = computed(() => `/organizations/${this.currentOrgStore?.organization()?.slug}/catalog/equipment`);
  readonly costLabels = [{key: "modules", label: "Solarmodule"}, {key: "inverter", label: "Wechselrichter"}, {key: "installation", label: "Montage und Montagesystem"}, {key: "roof_work", label: "Gerüst und Dacharbeiten"}, {key: "electrical", label: "Elektromaterial und Netzanschluss"}, {key: "other", label: "Weitere kalkulierte Kosten"}] as const;

  investmentRows(planning: AnalysisPlanning): { key: string; label: string; amount: number | null; share: number | null }[] {
    const snapshot = planning.resultSnapshot;
    const rows = snapshot
      ? this.costLabels.map((group) => ({ ...group, amount: snapshot.costs[group.key] as number | null }))
      : [{ key: 'pv', label: 'PV-Anlage gesamt', amount: planning.acquisitionCosts }];
    return [...rows, { key: 'storage', label: 'Stromspeicher', amount: planning.storageCosts ?? null }].map((row) => ({
      ...row,
      share: row.amount != null && planning.totalInvestment != null && planning.totalInvestment > 0
        ? row.amount / planning.totalInvestment * 100 : null,
    }));
  }

  readonly hoveredCost = signal<string | null>(null);
  readonly investmentChart = computed(() => {
    const planning = this.selectedPlanning();
    if (!planning) return [];
    const rows = this.investmentRows(planning);
    const total = rows.reduce((sum, row) => sum + (row.amount ?? 0), 0);
    // Same fixed order as the plugin's cost donut; validated for colour-vision deficiency on white.
    const colors = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7'];
    let offset = 0;
    return rows.map((row, index) => {
      const share = total > 0 ? (row.amount ?? 0) / total * 100 : 0;
      const segment = { ...row, color: colors[index % colors.length], offset, arc: Math.max(0, share - 0.8) };
      offset += share;
      return segment;
    });
  });
  readonly highlightedCost = computed(() => this.investmentChart().find(row => row.key === this.hoveredCost()));

  investmentPerKwp(planning: AnalysisPlanning): number | null {
    return planning.totalInvestment != null && planning.plantCapacity > 0
      ? planning.totalInvestment / planning.plantCapacity : null;
  }

  private readonly organizationId = computed(
    () => this.currentOrgStore?.organization()?.id ?? null,
  );
  private readonly analysisId = computed(
    () => this.analysisDetailStore?.analysisId() ?? null,
  );

  // ─── Planning selection ───────────────────────────────────────────────────

  selectedIndex = signal(0);

  hasPlannings = computed(() => this.plannings().length > 0);
  runningPlanningCount = computed(
    () => this.plannings().filter((planning) => planning.status === 'running').length,
  );

  showSummarySection = computed(
    () => this.section() === 'summary' || this.section() === 'all',
  );
  showTemplateSection = computed(
    () => this.section() === 'template' || this.section() === 'all',
  );
  showChartsSection = computed(
    () => this.section() === 'charts' || this.section() === 'all',
  );

  // ─── Chart data state ─────────────────────────────────────────────────────

  private readonly localChartDataCache = signal<
    Record<string, PlanningChartData | null>
  >({});

  isLoadingChart = computed(() => {
    if (this.chartDataProvider()) return false; // public page manages its own loading state
    // A different planning may be prefetching in the background.
    if (this.currentChartData() !== null) return false;
    return this.analysisDetailStore?.loadingChartData() ?? false;
  });

  chartError = signal<string | null>(null);

  currentChartData = computed<PlanningChartData | null>(() => {
    const planning = this.selectedPlanning();
    if (!planning) return null;
    const provider = this.chartDataProvider();
    if (provider) {
      return this.localChartDataCache()[planning.id] ?? null;
    }
    return (
      this.analysisDetailStore?.planningChartDataCache()[planning.id] ?? null
    );
  });

  selectedPlanning = computed<AnalysisPlanning | null>(() => {
    const plannings = this.sortedPlannings();
    if (!plannings.length) return null;

    const externalId = this.selectedPlanningId();
    if (externalId) {
      const externalSelection = plannings.find((p) => p.id === externalId);
      if (externalSelection) return externalSelection;
    }

    return plannings[this.selectedIndex()] ?? null;
  });

  // ─── Load chart data when selection changes ────────────────────────────────

  constructor() {
    effect(() => {
      const planning = this.selectedPlanning();
      if (
        !this.showSummarySection() ||
        this.mode() !== 'admin' ||
        planning?.status !== 'completed'
      ) {
        this.reportExportPlanningId = null;
        this.reportExport.set(null);
        this.stopReportExportPolling();
        return;
      }
      if (this.reportExportPlanningId === planning.id) return;
      this.reportExportPlanningId = planning.id;
      this.reportExport.set(null);
      void this.loadLatestReportExport(planning.id);
    });

    effect(() => {
      const showCharts = this.showChartsSection();
      const planning = this.selectedPlanning();
      const provider = this.chartDataProvider();

      if (!showCharts || !planning || planning.status !== 'completed') return;

      if (provider) {
        // Public mode: use provided function to load chart data
        if (this.localChartDataCache()[planning.id] !== undefined) return; // already loaded
        provider(planning.id)
          .then((data) => {
            this.localChartDataCache.update((c) => ({
              ...c,
              [planning.id]: data,
            }));
          })
          .catch(() =>
            this.chartError.set('Diagrammdaten konnten nicht geladen werden.'),
          );
      } else {
        // Admin mode: use store-based loading
        const orgId = this.organizationId();
        const analysisId = this.analysisId();
        if (!orgId || !analysisId) return;
        const cachedEntry =
          this.analysisDetailStore?.planningChartDataCache()[planning.id];
        if (cachedEntry && cachedEntry.total > 0) return; // already loaded with data

        this.chartError.set(null);
        this.analysisDetailStore
          ?.loadPlanningChartData(orgId, analysisId, planning.id)
          .catch(() =>
            this.chartError.set('Diagrammdaten konnten nicht geladen werden.'),
          );
      }
    });
  }

  ngOnDestroy(): void {
    this.stopReportExportPolling();
  }

  async onReportExportAction(planningId: string): Promise<void> {
    const current = this.reportExport();
    if (current?.status === 'completed') {
      await this.downloadReportExport(current);
      return;
    }
    if (current?.status === 'queued' || current?.status === 'running') return;
    await this.createReportExport(planningId);
  }

  async createReportExport(planningId: string): Promise<void> {
    const orgId = this.organizationId();
    const analysisId = this.analysisId();
    if (!orgId || !analysisId || this.reportExportBusy()) return;

    this.reportExportBusy.set(true);
    try {
      const report = await this.analysisService.createReportExport(
        orgId,
        analysisId,
        planningId,
      );
      this.reportExport.set(report);
      this.toastService.success('PDF-Erstellung gestartet');
      this.scheduleReportExportPoll(planningId);
    } catch {
      this.toastService.error('PDF konnte nicht erstellt werden');
    } finally {
      this.reportExportBusy.set(false);
    }
  }

  reportExportLabel(): string {
    if (this.reportExportBusy()) return 'PDF wird gestartet';
    const status = this.reportExport()?.status;
    if (!status) return 'PDF erstellen';
    return {
      queued: 'PDF wird erstellt',
      running: 'PDF wird erstellt',
      completed: 'PDF herunterladen',
      failed: 'PDF erneut versuchen',
    }[status];
  }

  private async loadLatestReportExport(planningId: string): Promise<void> {
    const orgId = this.organizationId();
    const analysisId = this.analysisId();
    if (!orgId || !analysisId) return;

    const request = ++this.reportExportRequest;
    try {
      const report = await this.analysisService.getLatestReportExport(
        orgId,
        analysisId,
        planningId,
      );
      if (request !== this.reportExportRequest) return;
      this.reportExport.set(report);
      if (report?.status === 'queued' || report?.status === 'running') {
        this.scheduleReportExportPoll(planningId);
      }
    } catch {
      if (request === this.reportExportRequest) this.reportExport.set(null);
    }
  }

  private scheduleReportExportPoll(planningId: string): void {
    this.stopReportExportPolling();
    this.reportExportPollTimer = window.setTimeout(async () => {
      await this.loadLatestReportExport(planningId);
    }, 2500);
  }

  private stopReportExportPolling(): void {
    if (this.reportExportPollTimer !== null) {
      window.clearTimeout(this.reportExportPollTimer);
      this.reportExportPollTimer = null;
    }
  }

  private async downloadReportExport(report: ReportExport): Promise<void> {
    const orgId = this.organizationId();
    const analysisId = this.analysisId();
    if (!orgId || !analysisId) return;
    try {
      const download = await this.analysisService.getReportExportDownload(
        orgId,
        analysisId,
        report.id,
      );
      const link = document.createElement('a');
      link.href = download.downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
    } catch {
      this.toastService.error('PDF konnte nicht heruntergeladen werden');
    }
  }

  selectPlanning(index: number): void {
    this.selectedIndex.set(index);
    const planning = this.sortedPlannings()[index];
    if (planning) {
      this.selectedPlanningIdChange.emit(planning.id);
    }
  }

  isPlanningSelected(planning: AnalysisPlanning, index: number): boolean {
    const externalId = this.selectedPlanningId();
    if (externalId) {
      return planning.id === externalId;
    }
    return this.selectedIndex() === index;
  }

  onDeletePlanning(planningId: string): void {
    this.deletePlanning.emit(planningId);
  }

  onSetPrimaryPlanning(planningId: string): void {
    this.setPrimaryPlanning.emit(planningId);
  }

  onCreatePlanning(): void {
    this.createPlanning.emit();
  }

  onRetryPlanning(planningId: string): void {
    this.retryPlanning.emit(planningId);
  }

  sortedPlannings = computed(() => {
    const list = [...this.plannings()];
    return list.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  });

  // ─── Computed chart options ────────────────────────────────────────────────

  /** 20-year cumulative cost comparison */
  costChartOptions = computed<LineChartOptions | null>(() => {
    const data = this.currentChartData();
    if (
      !data?.charts.costsWithoutLeaseCumulative ||
      !data?.charts.costsWithLeaseCumulative
    )
      return null;

    const years = Array.from({ length: 20 }, (_, i) => `Jahr ${i + 1}`);
    const withoutPv = data.charts.costsWithoutLeaseCumulative.values;
    const withPv = data.charts.costsWithLeaseCumulative.values;

    return {
      series: [
        { name: 'Ohne PV-Anlage', data: withoutPv },
        { name: 'Mit PV-Anlage', data: withPv },
      ],
      chart: {
        type: 'area',
        height: 280,
        fontFamily: 'inherit',
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      xaxis: {
        categories: years,
        labels: { rotate: -30, style: { fontSize: '11px' } },
        tickAmount: 10,
      },
      yaxis: {
        labels: {
          formatter: (val: number) => this.formatCurrencyShort(val),
          style: { fontSize: '11px' },
        },
      },
      stroke: { curve: 'straight', width: 2 },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.25,
          opacityTo: 0.05,
          stops: [0, 90, 100],
        },
      },
      tooltip: {
        y: { formatter: (val: number) => this.formatCurrency(val) },
        theme: 'light',
      },
      legend: { position: 'top', fontFamily: 'inherit', fontSize: '13px' },
      dataLabels: { enabled: false },
      grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
      colors: ['#cbd0dd', '#3363ff'],
    };
  });

  /** Monthly energy balance (stacked bar) */
  monthlyChartOptions = computed<BarChartOptions | null>(() => {
    const data = this.currentChartData();
    if (
      !data?.charts.coverageMonthConsumption ||
      !data?.charts.coverageMonthGeneration ||
      !data?.charts.coverageMonthOwnConsumption
    )
      return null;

    const consumption = data.charts.coverageMonthConsumption.values;
    const generation = data.charts.coverageMonthGeneration.values;
    const ownConsumption = data.charts.coverageMonthOwnConsumption.values;
    const gridPower = consumption.map((c, i) =>
      Math.max(0, c - (ownConsumption[i] ?? 0)),
    );
    const feedIn = generation.map((g, i) =>
      Math.max(0, g - (ownConsumption[i] ?? 0)),
    );

    return {
      series: [
        { name: 'Eigenverbrauch', data: ownConsumption },
        { name: 'Einspeisung', data: feedIn },
        { name: 'Netzstrom', data: gridPower },
      ],
      chart: {
        type: 'bar',
        height: 280,
        fontFamily: 'inherit',
        toolbar: { show: false },
        stacked: true,
      },
      xaxis: { categories: MONTHS_DE, labels: { style: { fontSize: '11px' } } },
      yaxis: {
        labels: {
          formatter: (val: number) => `${Math.round(val)} kWh`,
          style: { fontSize: '11px' },
        },
      },
      stroke: { width: 0 },
      fill: { opacity: 0.9 },
      tooltip: {
        y: { formatter: (val: number) => `${val.toFixed(0)} kWh` },
        theme: 'light',
      },
      legend: { position: 'top', fontFamily: 'inherit', fontSize: '13px' },
      dataLabels: { enabled: false },
      plotOptions: { bar: { borderRadius: 2, columnWidth: '70%' } },
      grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
      colors: ['#3363ff', '#7d9aff', '#cbd0dd'],
    };
  });

  /** Hourly daily profile (area) */
  dailyChartOptions = computed<LineChartOptions | null>(() => {
    const data = this.currentChartData();
    if (
      !data?.charts.coverageDayConsumption ||
      !data?.charts.coverageDayGeneration ||
      !data?.charts.coverageDayOwnConsumption
    )
      return null;

    return {
      series: [
        { name: 'Erzeugung', data: data.charts.coverageDayGeneration.values },
        {
          name: 'Eigenverbrauch',
          data: data.charts.coverageDayOwnConsumption.values,
        },
        { name: 'Verbrauch', data: data.charts.coverageDayConsumption.values },
      ],
      chart: {
        type: 'area',
        height: 280,
        fontFamily: 'inherit',
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      xaxis: {
        categories: HOURS,
        labels: { style: { fontSize: '11px' } },
        tickAmount: 12,
      },
      yaxis: {
        labels: {
          formatter: (val: number) => `${val.toFixed(2)} kW`,
          style: { fontSize: '11px' },
        },
      },
      stroke: { curve: 'straight', width: 2 },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.3,
          opacityTo: 0.02,
          stops: [0, 90, 100],
        },
      },
      tooltip: {
        y: { formatter: (val: number) => `${val.toFixed(3)} kW` },
        theme: 'light',
      },
      legend: { position: 'top', fontFamily: 'inherit', fontSize: '13px' },
      dataLabels: { enabled: false },
      grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
      colors: ['#3363ff', '#16a34a', '#cbd0dd'],
    };
  });

  hasChartsAvailable = computed(() => {
    const data = this.currentChartData();
    return !!data && data.total > 0;
  });

  // ─── Formatting helpers ───────────────────────────────────────────────────

  formatCurrency(value: number | null | undefined): string {
    if (value == null) return '—';
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  formatAnnualReturn(value: number | null): string {
    if (value == null || !Number.isFinite(Number(value))) return '—';
    // SolarInsight stores annual_return in percent, including values below 1%.
    return `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(Number(value))} %`;
  }

  formatCurrencyShort(value: number): string {
    if (Math.abs(value) >= 1000) {
      return `${(value / 1000).toFixed(1).replace('.', ',')} T€`;
    }
    return `${Math.round(value)} €`;
  }

  formatEuroPerKwh(value: number | null): string {
    if (value == null || !Number.isFinite(value)) return 'Nicht gespeichert';
    return `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(value)} EUR/kWh`;
  }

  formatNumber(value: number | null): string {
    if (value == null) return '—';
    return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(
      value,
    );
  }

  formatDate(date: Date | string | null | undefined): string {
    if (!date) return 'Unbekannt';
    const parsed = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(parsed.getTime())) return 'Unbekannt';
    return parsed.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }

  getPlanningLabel(planning: AnalysisPlanning, index: number): string {
    return `${this.getPlanningName(planning, index)} · ${this.getPlanningTypeLabel(planning)}`;
  }

  getPlanningName(planning: AnalysisPlanning, index: number): string {
    const defaultName = `Planung ${this.sortedPlannings().length - index}`;
    const requestedName = this.getPlanningRequest(planning)?.name.trim();
    return !requestedName || /^Planung\s+\d+$/i.test(requestedName)
      ? defaultName
      : `${defaultName} · ${requestedName}`;
  }

  getPlanningRequest(planning: AnalysisPlanning): PlanningRequest | undefined {
    const requests = this.analysis()?.metadata?.['planningNameRequests'];
    if (!Array.isArray(requests)) return undefined;
    const createdAt = new Date(planning.createdAt).getTime();
    return requests
      .filter((item: unknown): item is PlanningRequest => {
        if (!item || typeof item !== 'object') return false;
        const value = item as Record<string, unknown>;
        return typeof value['name'] === 'string' && typeof value['requestedAt'] === 'string';
      })
      .sort(
        (a, b) =>
          Math.abs(new Date(a.requestedAt).getTime() - createdAt) -
          Math.abs(new Date(b.requestedAt).getTime() - createdAt),
      )[0];
  }

  getPlanningStatusLabel(planning: AnalysisPlanning): string {
    return {
      running: 'Wird berechnet',
      completed: 'Fertig',
      failed: 'Fehlgeschlagen',
      cancelled: 'Abgebrochen',
    }[planning.status];
  }

  getPlanningStatusClass(planning: AnalysisPlanning): string {
    return {
      running: 'border-transparent bg-accent text-accent-foreground',
      completed: 'border-transparent bg-success-soft text-success',
      failed: 'border-transparent bg-danger-soft text-danger',
      cancelled: 'border-transparent bg-secondary text-muted-foreground',
    }[planning.status];
  }

  getPlanningStatusIcon(planning: AnalysisPlanning): string {
    if (planning.status === 'running') return 'lucideLoader';
    if (planning.status === 'completed') return 'lucideCheck';
    return 'lucideCircleAlert';
  }

  getPlanningPowerConsumption(planning: AnalysisPlanning): number | null {
    return planning.powerConsumption ?? null;
  }

  getPlanningElectricityPrice(planning: AnalysisPlanning): number | null {
    return planning.electricityPrice ?? null;
  }

  isPlanningStale(planning: AnalysisPlanning): boolean {
    const changedAt = this.analysis()?.metadata?.['planningInputsChangedAt'];
    if (typeof changedAt !== 'string') return false;
    return new Date(changedAt).getTime() > new Date(planning.updatedAt).getTime();
  }

  getPlanningTypeLabel(planning: AnalysisPlanning): string {
    const typeMap: Record<string, string> = {
      Buy: 'Kauf',
      Lease: 'Miete',
      buy: 'Kauf',
      lease: 'Miete',
      purchase: 'Kauf',
      rental: 'Miete',
    };
    const type = planning.template?.type || planning.type;
    return typeMap[type] ?? type;
  }
}
